import React, { useEffect, useState, useMemo } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CALENDLY_URL } from '../lib/siteConfig';
import SubscriberDetailDrawer from './SubscriberDetailDrawer';
import {
  Mail,
  Download,
  User,
  Tag,
  Calendar,
  BookOpen,
  Brain,
  KeyRound,
  CreditCard,
  Info,
  ChevronDown,
  Send,
  CalendarClock,
  Loader2,
  UserSearch,
} from 'lucide-react';
import { logger } from '../lib/logger';

// ---------------------------------------------------------------------------
// Mindset Quiz archetypes — used to render the badge per subscriber AND the
// collapsible reference card at the top of the page. Keep this in sync with
// /app/frontend/src/pages/quiz/quizData.js (single source of truth lives in
// the quiz definition, but the admin reference only needs the human summary).
// ---------------------------------------------------------------------------
const ARCHETYPE_META = {
  grinder: {
    name: 'The Grinder',
    headline: '"Locked in — sometimes too locked in."',
    summary: 'Mind never stops. Analyzes every play, replays every mistake. Doesn\u2019t need more focus \u2014 needs a way to turn the volume DOWN.',
    framework: '3-Breath Reset',
    framework_use: 'In-game reset, 30s, no equipment, anywhere.',
    growth: 'When you learn to quiet the mind on demand, your talent finally gets to play freely.',
    badgeClass: 'bg-purple-50 text-purple-800 border-purple-200',
    accent: '#7c3aed',
  },
  hopeful: {
    name: 'The Hopeful',
    headline: '"Talented \u2014 but waiting for permission."',
    summary: 'Got the skill. Put in the reps. Belief leaks out between practice and the whistle. Needs an identity they can stand on when the moment gets big.',
    framework: 'The ABC Confidence Stack',
    framework_use: 'Pre-performance, 90s. Anchor \u2192 Belief \u2192 Cue.',
    growth: 'Confidence isn\u2019t a feeling that shows up. It\u2019s a routine you run.',
    badgeClass: 'bg-sky-50 text-sky-800 border-sky-200',
    accent: '#0284c7',
  },
  phoenix: {
    name: 'The Phoenix',
    headline: '"High highs. Brutal lows. One mistake = one bad inning."',
    summary: 'When on, untouchable. One bad play and the whole thing slides. Doesn\u2019t need to care less \u2014 needs to reset between plays.',
    framework: 'Bounce-Back 3R',
    framework_use: 'Post-mistake, 30s. Release \u2192 Refocus \u2192 Re-engage.',
    growth: 'The next play is always bigger than the last one. Always.',
    badgeClass: 'bg-orange-50 text-orange-800 border-orange-200',
    accent: '#ea580c',
  },
  natural: {
    name: 'The Natural',
    headline: '"Gifted, but inconsistent. No routine = no floor."',
    summary: 'When feeling it, no one stops them. When not, can\u2019t find it. Needs a pre-game order that loads body, breath, and belief the same way every time.',
    framework: 'The Performance Pyramid',
    framework_use: 'Pre-game order, 10\u201315 min. Body \u2192 Breath \u2192 Belief.',
    growth: 'Consistency isn\u2019t boring. It\u2019s how you stop leaving your A-game in the locker room.',
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    accent: '#059669',
  },

  // ---- Parent archetypes (Parent Mindset Quiz) ----------------------------
  fixer: {
    name: 'The Fixer (Parent)',
    headline: '"You see the problem. You want to solve it. Right now."',
    summary: 'Loves through action. Athlete starts hiding struggle because every share triggers a fix. Needs to listen first, fix never (or much later).',
    framework: 'The 3-Question Car Ride',
    framework_use: 'After competition. Three open questions, zero solutions.',
    growth: 'Your love language is fixing. Theirs is being heard. Trade for ten minutes.',
    badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
    accent: '#b45309',
  },
  cheerleader: {
    name: 'The Cheerleader (Parent)',
    headline: '"Always positive. Athlete has stopped trusting it."',
    summary: 'Loudest in the stands. Praise lost weight because it never differentiates. Needs specific positivity their athlete can stand on.',
    framework: 'The Specific Praise Protocol',
    framework_use: 'Drive home. Name behavior, name what it took, skip outcome.',
    growth: 'Generic praise is white noise. Specific praise is fuel.',
    badgeClass: 'bg-pink-50 text-pink-800 border-pink-200',
    accent: '#be185d',
  },
  hoverer: {
    name: 'The Hoverer (Parent)',
    headline: '"Their game lives in your stomach."',
    summary: 'Loves so much that athlete\u2019s performance has become their nervous system. Needs to keep fire WHILE staying off their back.',
    framework: 'The 90-Second Sideline Reset',
    framework_use: 'During the game. Between innings/quarters/sets.',
    growth: 'Your job is to love them the same on the way home whether they went 4-for-4 or 0-for-4. Your body has to learn that first.',
    badgeClass: 'bg-rose-50 text-rose-800 border-rose-200',
    accent: '#be123c',
  },
  stoic: {
    name: 'The Stoic (Parent)',
    headline: '"Steady. Calm. Kid isn\u2019t sure you noticed."',
    summary: 'Doesn\u2019t get loud, doesn\u2019t get rattled. Missing the witness piece. Needs ONE sentence per game that tells athlete they were seen.',
    framework: 'The Witness Sentence',
    framework_use: 'Within an hour of the game. One sentence. Specific. Quiet.',
    growth: 'Steady doesn\u2019t mean silent. One sentence does more than a speech.',
    badgeClass: 'bg-slate-50 text-slate-800 border-slate-200',
    accent: '#475569',
  },

  // ---- Coach archetypes (Coach Mindset Quiz) ------------------------------
  drill_sergeant: {
    name: 'The Drill Sergeant (Coach)',
    headline: '"They\u2019re scared to disappoint you."',
    summary: 'Builds standards, brings intensity. Athletes hide mistakes instead of bringing them. Needs a connection window so they trust enough to fail in front of you.',
    framework: 'The Connection Window',
    framework_use: 'First 90 seconds of practice. Trust without losing intensity.',
    growth: 'They\u2019ll run through a wall for you, but only if they believe you\u2019ll catch them when they fall short of one.',
    badgeClass: 'bg-red-50 text-red-800 border-red-200',
    accent: '#b91c1c',
  },
  mentor: {
    name: 'The Mentor (Coach)',
    headline: '"They love playing for you. Execution gap keeps showing up."',
    summary: 'Culture is real. Beliefs don\u2019t turn into reps in games. Needs a bridge from belief to behavior under pressure.',
    framework: 'The Standards Bridge',
    framework_use: 'In-practice. Translate culture words into observable behaviors.',
    growth: 'Belief without behavior is a poster. Belief tied to behavior is a program.',
    badgeClass: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    accent: '#4338ca',
  },
  tactician: {
    name: 'The Tactician (Coach)',
    headline: '"They feel like pieces."',
    summary: 'Out-prepares everyone. Athletes execute instead of own. Needs to teach the WHY before the HOW so they adapt when it cracks.',
    framework: 'The Why Stack',
    framework_use: 'Pre-practice briefing. What \u2192 Why \u2192 What it lets us do.',
    growth: 'A team that executes your plan will win games. A team that understands your plan will win seasons.',
    badgeClass: 'bg-teal-50 text-teal-800 border-teal-200',
    accent: '#0f766e',
  },
  players_coach: {
    name: 'The Player\u2019s Coach (Coach)',
    headline: '"You\u2019d rather not have the hard conversation."',
    summary: 'Trust is real. Hard conversations get softened. Slippage shows in habits. Needs to deliver hard truth WITHOUT burning the trust.',
    framework: 'The Honest Mirror',
    framework_use: '1-on-1 feedback. Witness \u2192 Standard \u2192 Belief.',
    growth: 'The kids who love you the most are the ones who can take the most honest version of you. Don\u2019t water it down.',
    badgeClass: 'bg-cyan-50 text-cyan-800 border-cyan-200',
    accent: '#0e7490',
  },
};

function archetypeMeta(key) {
  if (!key) return null;
  return ARCHETYPE_META[String(key).toLowerCase()] || null;
}

// Source bucket metadata — kept in one map so badges, filter chips and
// stat tiles all stay consistent.
const SOURCE_META = {
  newsletter: {
    label: 'Newsletter',
    badge: 'bg-slate-50 text-slate-700 border-slate-200',
    chip: 'bg-slate-100 text-slate-900 border-slate-300',
    icon: Mail,
  },
  quiz: {
    label: 'Mindset Quiz',
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    chip: 'bg-amber-100 text-amber-900 border-amber-300',
    icon: Brain,
  },
  playbook: {
    label: 'Playbook',
    badge: 'bg-[#E6F4F6] text-[#003a40] border-[#004C54]/20',
    chip: 'bg-[#CFEAEC] text-[#003a40] border-[#004C54]/30',
    icon: BookOpen,
  },
  account: {
    label: 'Account',
    badge: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    chip: 'bg-indigo-100 text-indigo-900 border-indigo-300',
    icon: KeyRound,
  },
  bootcamp: {
    label: 'Bootcamp',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    chip: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    icon: CreditCard,
  },
};

const FILTER_ORDER = ['newsletter', 'quiz', 'playbook', 'account', 'bootcamp'];

function metaFor(bucket) {
  return SOURCE_META[bucket] || {
    label: (bucket || '').replace(/_/g, ' ') || 'Unknown',
    badge: 'bg-slate-50 text-slate-500 border-slate-200',
    chip: 'bg-slate-100 text-slate-700 border-slate-300',
    icon: Tag,
  };
}

function toCsv(rows) {
  const headers = [
    'first_name',
    'email',
    'sources',
    'source',
    'source_detail',
    'quiz_archetype',
    'quiz_sport',
    'quiz_audience',
    'created_at',
    'latest_activity_at',
    'tier',
    'amount',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = Array.isArray(v) ? v.join('|') : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.join(',');
  const lines = rows.map((r) => headers.map((h) => escape(r[h])).join(','));
  return [head, ...lines].join('\n');
}

function StatTile({ icon: Icon, label, value, color = '#004C54' }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color }}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-[18px] font-bold text-[#003a40] leading-tight">{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-foreground/55 font-semibold">{label}</div>
      </div>
    </div>
  );
}

export default function AdminSubscribers() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState({ active: 0, by_source: {} });
  const [q, setQ] = useState('');
  const [activeFilters, setActiveFilters] = useState(new Set()); // empty = show all
  const [archetypeRefOpen, setArchetypeRefOpen] = useState(false);
  // Per-row expanded state: emails whose archetype detail panel is open below
  // their row (so consult prep can read the full breakdown without leaving the
  // table). Stored as a Set of normalized emails.
  const [expandedEmails, setExpandedEmails] = useState(() => new Set());
  // Per-email fetched quiz attempts (full answers + scores).
  // Map: email -> { loading: bool, attempts: [...], error: string | null }
  const [quizHistory, setQuizHistory] = useState({});
  // Per-email pending state for the "resend framework PDF" action so each
  // row's button can show its own spinner / disabled state.
  const [resendingEmails, setResendingEmails] = useState(() => new Set());

  // Phase 29 — comprehensive 360° drawer. Stores the open email; null = closed.
  const [drawerEmail, setDrawerEmail] = useState(null);
  const openDrawer = (email) => setDrawerEmail(email);
  const closeDrawer = () => setDrawerEmail(null);

  const toggleExpanded = (email) => {
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const loadQuizHistory = async (email) => {
    if (!email) return;
    // If we already have data or a fetch in flight, do nothing.
    if (quizHistory[email]?.attempts || quizHistory[email]?.loading) return;
    setQuizHistory((prev) => ({ ...prev, [email]: { loading: true, attempts: null, error: null } }));
    try {
      const data = await adminApi.subscriberQuizHistory(email);
      setQuizHistory((prev) => ({
        ...prev,
        [email]: { loading: false, attempts: data.attempts || [], error: null },
      }));
    } catch (e) {
      logger.warn('quiz history load failed', e);
      setQuizHistory((prev) => ({
        ...prev,
        [email]: { loading: false, attempts: [], error: e?.message || 'Could not load quiz answers.' },
      }));
    }
  };

  const handleResendFramework = async (email) => {
    if (!email) return;
    if (resendingEmails.has(email)) return;
    setResendingEmails((prev) => {
      const next = new Set(prev);
      next.add(email);
      return next;
    });
    try {
      const data = await adminApi.resendFrameworkEmail(email);
      if (data?.ok) {
        toast.success(`Sent ${data.archetype_label || 'framework'} email to ${email}.`);
      } else {
        toast.error('Email send did not succeed. Check Resend logs.');
      }
    } catch (e) {
      logger.warn('resend framework failed', e);
      toast.error(e?.message || 'Could not resend the framework email.');
    } finally {
      setResendingEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    }
  };

  // Calendly link for the "schedule consult" quick-link.
  // Single source of truth lives in /app/frontend/src/lib/siteConfig.js.

  useEffect(() => {
    adminApi
      .listSubscribers()
      .then((d) => {
        setItems(d.subscribers || []);
        setStats({ active: d.active || 0, by_source: d.by_source || {} });
      })
      .catch((e) => {
        logger.error('subscribers load failed', e);
        setItems([]);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      // Filter chips: row passes if it has ANY of the selected source buckets.
      if (activeFilters.size > 0) {
        const rowSources = new Set(r.sources || []);
        const matchesFilter = [...activeFilters].some((b) => rowSources.has(b));
        if (!matchesFilter) return false;
      }
      // Free-text search across name/email/sources/detail/archetype.
      if (!needle) return true;
      const am = archetypeMeta(r.quiz_archetype);
      const haystack = [
        r.first_name,
        r.email,
        (r.sources || []).join(' '),
        r.source_detail,
        r.tier,
        r.quiz_archetype,
        am ? am.name : '',
        r.quiz_sport,
        r.quiz_audience,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [items, q, activeFilters]);

  const toggleFilter = (bucket) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) {
        next.delete(bucket);
      } else {
        next.add(bucket);
      }
      return next;
    });
  };

  const clearFilters = () => setActiveFilters(new Set());

  const download = () => {
    if (!filtered) return;
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apex_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (filtered === null) {
    return (
      <div className="space-y-2" data-testid="admin-subs-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const total = items.length;
  const namedCount = items.filter((s) => (s.first_name || '').trim()).length;
  const topSourceKey = Object.entries(stats.by_source || {}).sort((a, b) => b[1] - a[1])[0];
  const topSource = topSourceKey
    ? `${metaFor(topSourceKey[0]).label} (${topSourceKey[1]})`
    : '—';

  return (
    <div className="space-y-5" data-testid="admin-subscribers-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#004C54]" />
          <h2 className="font-heading text-2xl font-bold">Subscribers</h2>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Search name, email, source…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 w-64"
            data-testid="admin-subs-search"
          />
          <Button
            onClick={download}
            variant="outline"
            disabled={!filtered.length}
            data-testid="admin-subs-export"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats row — at-a-glance summary so the page reads before any scrolling */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-subs-stats">
        <StatTile icon={Mail} label="Total" value={total} />
        <StatTile icon={User} label="Active" value={stats.active} color="#2F7A4F" />
        <StatTile icon={Tag} label="With name" value={namedCount} color="#4FBCC7" />
        <StatTile icon={Calendar} label="Top source" value={topSource} color="#003a40" />
      </div>

      {/* Mindset Quiz archetype reference card — collapsed by default. Useful
          before a consult: open it to remind yourself what each archetype's
          dominant struggle is and which framework to prescribe. */}
      <div
        className="rounded-xl border border-[#004C54]/15 bg-white overflow-hidden"
        data-testid="admin-subs-archetype-reference"
      >
        <button
          type="button"
          onClick={() => setArchetypeRefOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F6F7F8] transition-colors"
          aria-expanded={archetypeRefOpen}
          data-testid="admin-subs-archetype-reference-toggle"
        >
          <span className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-[#004C54]" />
            <span className="font-semibold text-[14px] text-[#0B0C0D]">Mindset Quiz \u2014 Archetype Reference</span>
            <span className="text-[11.5px] text-foreground/55">(open before a consult)</span>
          </span>
          <ChevronDown
            className={`h-4 w-4 text-foreground/55 transition-transform ${archetypeRefOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {archetypeRefOpen && (
          <div className="border-t border-[#004C54]/10 px-4 py-4 grid sm:grid-cols-2 gap-3">
            {Object.entries(ARCHETYPE_META).map(([key, meta]) => (
              <div
                key={key}
                className="rounded-lg border bg-white p-3.5"
                style={{ borderColor: `${meta.accent}33` }}
                data-testid={`admin-subs-archetype-ref-${key}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: meta.accent }}
                  />
                  <h3 className="font-bold text-[14px] text-[#0B0C0D]">{meta.name}</h3>
                </div>
                <p className="text-[12.5px] italic text-foreground/65 mb-2">{meta.headline}</p>
                <p className="text-[13px] text-foreground/80 leading-relaxed mb-2.5">{meta.summary}</p>
                <div className="text-[12px] text-foreground/75 space-y-0.5">
                  <p>
                    <span className="font-semibold text-[#004C54]">Framework:</span>{' '}
                    {meta.framework}
                  </p>
                  <p className="text-foreground/60">{meta.framework_use}</p>
                </div>
                <p className="text-[12px] italic mt-2.5 pt-2 border-t border-foreground/10 text-foreground/70">
                  &ldquo;{meta.growth}&rdquo;
                </p>
              </div>
            ))}
            <p className="sm:col-span-2 text-[11.5px] text-foreground/55 italic text-center pt-1">
              Subscribers who completed the quiz have their archetype badge shown next to their source tags below.
            </p>
          </div>
        )}
      </div>

      {/* Source filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="admin-subs-filters">
        <span className="text-[11.5px] uppercase tracking-wider text-foreground/55 font-semibold mr-1">
          Filter:
        </span>
        {FILTER_ORDER.map((b) => {
          const meta = metaFor(b);
          const count = stats.by_source?.[b] || 0;
          const isActive = activeFilters.has(b);
          const Icon = meta.icon;
          return (
            <button
              key={b}
              type="button"
              onClick={() => toggleFilter(b)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
                isActive
                  ? meta.chip
                  : 'bg-white text-foreground/70 border-border hover:bg-[#F6F7F8]'
              }`}
              data-testid={`admin-subs-filter-${b}`}
              aria-pressed={isActive}
            >
              <Icon className="h-3.5 w-3.5" />
              {meta.label}
              <span className={`tabular-nums text-[11px] ${isActive ? 'opacity-80' : 'text-foreground/50'}`}>
                {count}
              </span>
            </button>
          );
        })}
        {activeFilters.size > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[12px] text-[#004C54] underline-offset-2 hover:underline ml-1"
            data-testid="admin-subs-filter-clear"
          >
            Clear
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-10 text-center text-foreground/60" data-testid="admin-subs-empty">
            {q || activeFilters.size > 0
              ? 'No subscribers match the current filters.'
              : 'No subscribers yet.'}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border overflow-hidden">
          {/* Desktop table header */}
          <div className="hidden sm:grid grid-cols-[1.1fr_1.6fr_1fr_180px] gap-3 px-4 py-2.5 bg-[#F6F7F8] text-[11px] uppercase tracking-wider text-foreground/60 font-semibold border-b border-border">
            <div>Name</div>
            <div>Email</div>
            <div>Sources</div>
            <div>Last activity</div>
          </div>
          <ul className="divide-y divide-border" data-testid="admin-subs-list">
            {filtered.map((s) => {
              const initial = (s.first_name || s.email || '?').trim().charAt(0).toUpperCase();
              const sources = s.sources && s.sources.length > 0 ? s.sources : [s.source || 'newsletter'];
              const lastActivity = s.latest_activity_at || s.created_at;
              const am = archetypeMeta(s.quiz_archetype);
              const isExpanded = expandedEmails.has(s.email);
              return (
                <li
                  key={s.email}
                  className="px-4 py-3 text-[13.5px]"
                  data-testid={`admin-subs-row-${s.email}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-[1.1fr_1.6fr_1fr_180px] gap-1.5 sm:gap-3 items-center">
                    {/* Name + avatar */}
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-[12px] font-bold"
                        style={{ background: s.first_name ? '#004C54' : '#94a3b8' }}
                        aria-hidden="true"
                      >
                        {initial}
                      </div>
                      <span
                        className={`truncate font-medium ${
                          s.first_name ? 'text-foreground' : 'text-foreground/50 italic'
                        }`}
                      >
                        {s.first_name || 'No name'}
                      </span>
                    </div>
                    {/* Email */}
                    <div className="truncate text-foreground/80 sm:text-foreground">
                      <a href={`mailto:${s.email}`} className="hover:text-[#004C54] hover:underline">
                        {s.email}
                      </a>
                    </div>
                    {/* Source badges (one per bucket) + quiz archetype + bootcamp tier */}
                    <div className="flex flex-wrap gap-1.5">
                      {sources.map((b) => {
                        const meta = metaFor(b);
                        return (
                          <Badge
                            key={b}
                            variant="outline"
                            className={`${meta.badge} text-[11px] font-medium`}
                          >
                            {meta.label}
                          </Badge>
                        );
                      })}
                      {am && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(s.email)}
                          className={`inline-flex items-center gap-1 rounded-md border ${am.badgeClass} px-1.5 py-0.5 text-[11px] font-medium transition-colors hover:brightness-95 cursor-pointer`}
                          title={isExpanded ? 'Hide archetype detail' : 'Show full archetype breakdown'}
                          aria-expanded={isExpanded}
                          data-testid={`admin-subs-archetype-${s.email}`}
                        >
                          <Brain className="h-3 w-3" />
                          {am.name}
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      )}
                      {s.tier && (
                        <Badge
                          variant="outline"
                          className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[11px] font-medium"
                        >
                          {s.tier === 'earlybird' ? 'Early-bird' : 'Standard'}
                          {s.amount ? ` \u00b7 ${s.amount}` : ''}
                        </Badge>
                      )}
                    </div>
                    {/* Last activity */}
                    <div className="text-foreground/55 text-[12.5px] flex items-center justify-between gap-2 flex-wrap">
                      <span>
                        {lastActivity ? new Date(lastActivity).toLocaleString() : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => openDrawer(s.email)}
                        className="inline-flex items-center gap-1 rounded-md border border-[#004C54]/30 text-[#004C54] hover:bg-[#004C54]/10 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
                        data-testid={`admin-subs-view-${s.email}`}
                        title="Open the 360° profile drawer for this subscriber"
                      >
                        <UserSearch className="h-3 w-3" />
                        View
                      </button>
                    </div>
                  </div>

                  {/* Archetype detail panel — opens when the user clicks the
                      archetype badge. Gives a consult-ready breakdown without
                      navigating away from the list. */}
                  {am && isExpanded && (
                    <div
                      className="mt-3 ml-0 sm:ml-[40px] rounded-lg border-l-4 p-4 bg-[#FAFAFB]"
                      style={{ borderLeftColor: am.accent }}
                      data-testid={`admin-subs-archetype-detail-${s.email}`}
                    >
                      <div className="flex items-start gap-2 flex-wrap">
                        <div
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white"
                          style={{ background: am.accent }}
                        >
                          <Brain className="h-3 w-3" /> {am.name}
                        </div>
                        {s.quiz_sport && s.quiz_sport !== 'general' && (
                          <span className="text-[11px] text-foreground/55 uppercase tracking-wider">
                            Sport: {s.quiz_sport}
                          </span>
                        )}
                      </div>
                      <p
                        className="mt-2 italic text-[13px]"
                        style={{ color: am.accent }}
                      >
                        {am.headline}
                      </p>
                      <p className="mt-2 text-[13px] leading-relaxed text-foreground/85">
                        {am.summary}
                      </p>
                      <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2 items-start">
                        <div className="rounded-md bg-white border border-border p-2.5">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">
                            Framework to run first
                          </div>
                          <div className="font-semibold text-foreground mt-0.5">
                            {am.framework}
                          </div>
                          <div className="text-[12px] text-foreground/65 mt-0.5">
                            {am.framework_use}
                          </div>
                        </div>
                      </div>
                      <p
                        className="mt-3 text-[12.5px] italic font-medium"
                        style={{ color: am.accent }}
                      >
                        “{am.growth}”
                      </p>

                      {/* Quick action row — resend the audience-specific
                          framework PDF email and a direct Calendly link to
                          schedule a consult with this person. */}
                      <div className="mt-3 flex flex-wrap gap-2 items-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendFramework(s.email)}
                          disabled={resendingEmails.has(s.email)}
                          className="border-[#004C54] text-[#004C54] hover:bg-[#004C54]/10 text-[12px] h-8"
                          data-testid={`admin-subs-resend-framework-${s.email}`}
                        >
                          {resendingEmails.has(s.email) ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              Sending…
                            </>
                          ) : (
                            <>
                              <Send className="h-3.5 w-3.5 mr-1.5" />
                              Resend framework PDF
                            </>
                          )}
                        </Button>
                        <a
                          href={`${CALENDLY_URL}?email=${encodeURIComponent(s.email)}${s.first_name ? `&name=${encodeURIComponent(s.first_name)}` : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-subs-schedule-consult-${s.email}`}
                          className="inline-flex"
                        >
                          <Button
                            type="button"
                            size="sm"
                            className="bg-[#004C54] hover:bg-[#003a40] text-white text-[12px] h-8"
                          >
                            <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                            Schedule consult
                          </Button>
                        </a>
                      </div>

                      {/* Quiz answers quick-link — lazily fetches the full
                          per-question selections + scores so you can see
                          exactly how this person answered before a consult. */}
                      {(() => {
                        const hist = quizHistory[s.email];
                        const loaded = !!hist?.attempts;
                        return (
                          <div className="mt-3 pt-3 border-t border-border/60">
                            {!loaded && !hist?.loading && (
                              <button
                                type="button"
                                onClick={() => loadQuizHistory(s.email)}
                                className="text-[12px] font-semibold text-[#004C54] underline-offset-2 hover:underline inline-flex items-center gap-1"
                                data-testid={`admin-subs-load-answers-${s.email}`}
                              >
                                View full quiz answers →
                              </button>
                            )}
                            {hist?.loading && (
                              <div className="text-[12px] text-foreground/55">Loading answers…</div>
                            )}
                            {hist?.error && (
                              <div className="text-[12px] text-red-700">{hist.error}</div>
                            )}
                            {loaded && hist.attempts.length === 0 && (
                              <div className="text-[12px] text-foreground/55 italic">
                                No quiz attempts on file.
                              </div>
                            )}
                            {loaded && hist.attempts.length > 0 && (
                              <div className="space-y-3" data-testid={`admin-subs-quiz-attempts-${s.email}`}>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/55">
                                  Quiz attempts ({hist.attempts.length})
                                </div>
                                {hist.attempts.map((att, idx) => {
                                  const total = (att.answers || []).length;
                                  const sortedScores = Object.entries(att.scores || {})
                                    .sort((a, b) => b[1] - a[1]);
                                  return (
                                    <div
                                      key={att.id || idx}
                                      className="rounded-md bg-white border border-border p-2.5"
                                    >
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="text-[12px] font-semibold text-foreground">
                                          Attempt #{hist.attempts.length - idx}
                                          {idx === 0 && (
                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-[#004C54] font-bold">
                                              · most recent
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-[11px] text-foreground/55">
                                          {att.created_at ? new Date(att.created_at).toLocaleString() : ''}
                                        </div>
                                      </div>
                                      <div className="mt-1 text-[12px] text-foreground/80">
                                        <span className="font-medium">{att.archetype_label || att.archetype}</span>
                                        {att.audience && (
                                          <span className="text-foreground/55"> · {att.audience}</span>
                                        )}
                                        {att.sport && att.sport !== 'general' && (
                                          <span className="text-foreground/55"> · {att.sport}</span>
                                        )}
                                      </div>
                                      {/* Per-question picks as small pills */}
                                      {total > 0 && (
                                        <div className="mt-2">
                                          <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-1">
                                            Per-question picks
                                          </div>
                                          <div className="flex flex-wrap gap-1">
                                            {(att.answers || []).map((a, qi) => (
                                              <span
                                                key={`q-${qi}-${a}`}
                                                className="inline-flex items-center gap-1 rounded border border-border bg-[#FAFAFB] px-1.5 py-0.5 text-[10.5px]"
                                              >
                                                <span className="font-bold text-foreground/55">Q{qi + 1}</span>
                                                <span className="font-medium text-foreground">{a}</span>
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {/* Score breakdown */}
                                      {sortedScores.length > 0 && (
                                        <div className="mt-2">
                                          <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-1">
                                            Score breakdown
                                          </div>
                                          <div className="flex flex-wrap gap-1.5">
                                            {sortedScores.map(([k, v]) => (
                                              <span
                                                key={k}
                                                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium ${
                                                  k === att.archetype
                                                    ? 'bg-[#004C54] text-white'
                                                    : 'bg-[#F1F2F4] text-foreground/75'
                                                }`}
                                              >
                                                {k}: {v}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Helper note */}
      <p className="text-[12px] text-foreground/55 italic" data-testid="admin-subs-name-note">
        Anyone who gave you their email through any funnel (newsletter form, Mindset Quiz, Playbook download, account signup, or Bootcamp purchase) appears here once, with badges for every funnel they touched. Click an archetype badge to expand the consult-prep breakdown, or click <span className="font-semibold">View</span> on any row to open the full 360° profile (app activity, chats, journals, nurture, payments).
      </p>

      {/* Phase 29 — 360° drawer (chats, journals, payments, nurture, etc.) */}
      <SubscriberDetailDrawer
        open={!!drawerEmail}
        email={drawerEmail}
        onOpenChange={(open) => { if (!open) closeDrawer(); }}
      />
    </div>
  );
}
